/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { Root } from "@radix-ui/react-slider";
import type { ComponentProps } from "react";

import {
  colors,
  numericPercentages,
  radius,
  spacing,
} from "../../../../../global/emotion";
import type { Color } from "../../../../types/color.emotion.types";
import { Slider } from "./Slider.emotion";

const fallthroughStyle: Interpolation = {
  background: colors.transparent,
};
const containerStyle: Interpolation = {
  height: spacing.s,
  position: "relative",
  width: numericPercentages[100],
};
const containerXLStyle: Interpolation = {
  height: spacing.m,
};
const slidersStyle: Interpolation = {
  inset: 0,
  position: "absolute",
};
const otherRangesStyle: Interpolation = {
  borderBottomRightRadius: radius.none,
  borderTopRightRadius: radius.none,
  minWidth: spacing.s,
};

type MultiSliderProps = Omit<
  ComponentProps<typeof Root>,
  "value" | "defaultValue"
> & {
  values?: number[];
  defaultValues?: number[];
  size?: "small" | "large" | "xl";
  onColor?: boolean;
  colors?: Color[];
  trackCSS?: Interpolation;
  rangeCSSArrays?: Interpolation[];
};

/**
 * This is different from a range Slider in that the first value is the end of the first slider, and subsequent
 * values are the ends of subsequent sliders. They all start at 0.
 */
export const MultiSlider = ({
  values,
  defaultValues,
  size,
  onColor,
  colors,
  trackCSS,
  rangeCSSArrays,
  ...props
}: MultiSliderProps) => {
  const [firstValue, ...otherValues] = [...(values ?? [])].reverse();
  const [firstDefault, ...otherDefaults] = [...(defaultValues ?? [])].reverse();
  const [firstColor, ...otherColors] = [...(colors ?? [])].reverse();
  const [firstRangeArray, ...otherRangeArrays] = [
    ...(rangeCSSArrays ?? []),
  ].reverse();
  return (
    <div css={[containerStyle, size === "xl" ? containerXLStyle : null]}>
      <Slider
        key={"largestSlider"}
        size={size}
        css={slidersStyle}
        trackCSS={trackCSS}
        rangeCSS={firstRangeArray}
        color={firstColor as any}
        value={firstValue}
        defaultValue={firstDefault}
        onColor={onColor}
        {...props}
      />
      {otherValues.map((val, idx) => (
        <Slider
          key={`slider${idx}`}
          size={size}
          css={slidersStyle}
          trackCSS={fallthroughStyle}
          rangeCSS={[otherRangesStyle, otherRangeArrays[idx]]}
          value={val}
          defaultValue={otherDefaults[idx]}
          color={otherColors[idx] as any}
          onColor={onColor}
          {...props}
        />
      ))}
    </div>
  );
};
